import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { PERSONALITY_KEYS, PersonalityKey } from '../data/tables/NormalNpcData';
import { GREETING_LINES, GREETING_REPLY_ME, GREETING_REPLY_NPC, GUEST_INVITE_REPLY_ME, GUEST_INVITE_REPLY_NPC } from '../data/tables/EncounterTexts';
import { guestCameoSystem } from './GuestCameoSystem';
import { NpcGreeting } from '../data/types';

/**
 * NPC 主动打招呼系统（docs/玩法设计.md §八 + 实现说明迁存章 A3）
 * 月初结算对每个已结识 NPC 独立掷概率；命中者把招呼语压进 player.greetings 队列，
 * 人脉页行 + 底部导航出未读红点，聊天页可见招呼气泡，玩家回应 +1 好感（不占聊天额度）。
 * 概率 = 好感档基础概率；咖位是准入门槛（Lv1~3 需熟悉 / Lv4~6 需认识 / Lv7+ 初识也行），
 * 且 Lv7+ 对初识/认识低档按咖位抬升概率（"发达了就有人上赶着巴结"）。
 * 防刷屏：全局每月最多 GREETING_MONTHLY_MAX 条，超出的命中按概率降序截断。
 */

function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

/** 回应结果（供聊天页追加两口气泡） */
export interface GreetingReplyResult {
    ok: boolean;
    meText: string;
    npcText: string;
    gain: number;
}

export class GreetingSystem {
    private static instance: GreetingSystem;

    private constructor() { }

    static getInstance(): GreetingSystem {
        if (!this.instance) {
            this.instance = new GreetingSystem();
        }
        return this.instance;
    }

    /**
     * 月初掷骰（GameLoop 在 advanceMonth 之后调用，命中消息挂在新月份）：
     * 先清算上月——未回应的招呼整体过期（红点/待回应随之消失），已回应的保留作聊天历史；
     * 再对全部已结识 NPC（好感 ≥0）逐个判门槛 + 掷概率，命中表按概率降序截断后入队
     */
    roll(): void {
        const state = GameState.getInstance();
        const p = state.player;
        // 月初全清（2026-10-25 茄子令「不管回复或者没回复的打招呼都清」）：招呼气泡（含已回应的
        // 定格回话）与客串邀请只在当月可见，聊天历史不跨月堆积——与直签消息/经纪人通知同一卫生口径。
        // 旧口径「已回应/已抉择的保留跨月回放」（批次1 问题⑫＋客串批①）由本令覆盖作废。
        // 未抉择的客串邀请一并失效（没赶上=没了——接受结算只在 picked 首次落定时发生过才有账）。
        p.greetings = [];
        const rank = p.rank;
        // 咖位准入门槛：Lv1~3 需熟悉(40) / Lv4~6 需认识(20) / Lv7+ 初识(0)即可
        let gate: number = GameConfig.GREETING_RANK_GATE_DEFAULT;
        for (const g of GameConfig.GREETING_RANK_GATE) {
            if (rank <= g.maxRank) { gate = g.minFavor; break; }
        }
        const hits: { npcId: string; prob: number }[] = [];
        for (const id of this.metNpcIds()) {
            const favor = state.getFavor(id);
            if (favor < gate) continue;
            let prob = this.tierProb(favor);
            // Lv7+ 低档（初识/认识）按咖位抬升（取满足 minRank 的最高档：bump 表按 minRank 升序，倒着找）
            if (rank >= 7 && favor < 40) {
                for (let i = GameConfig.GREETING_LOWTIER_BUMP.length - 1; i >= 0; i--) {
                    const b = GameConfig.GREETING_LOWTIER_BUMP[i];
                    if (rank >= b.minRank) { prob = Math.max(prob, b.prob); break; }
                }
            }
            if (Math.random() * 100 < prob) hits.push({ npcId: id, prob });
        }
        if (hits.length === 0) return;
        // 全局每月上限：按命中概率降序取前 N（更熟/更想巴结的先发）
        hits.sort((a, b) => b.prob - a.prob);
        const capped = hits.slice(0, GameConfig.GREETING_MONTHLY_MAX);
        for (const h of capped) {
            const raw = pick(this.greetingPool(h.npcId));
            // 特殊 NPC 的 greetingTexts 支持 NPC| 前缀 + | 分段 + // 连发（与 chatTexts 同款语法）
            // 格式：NPC|开场白|玩家回复|NPC再回复（可含 // 连发）
            const stripped = raw.startsWith('NPC|') ? raw.slice(4) : raw;
            const parts = stripped.split('|').map(s => s.trim());
            const text = parts[0] ?? raw;
            const replyText = parts[1] ?? pick(GREETING_REPLY_ME);
            const replyNpcText = parts[2];  // 缺省=undefined → respond() 走通用池；可含 // 连发
            p.greetings.push({
                npcId: h.npcId,
                text,
                replyText,
                replyNpcText,
                year: p.year,
                month: p.month,
                read: false,
                replied: false,
            });
        }
        if (p.greetings.length > GameConfig.GREETING_QUEUE_MAX) {
            p.greetings = p.greetings.slice(-GameConfig.GREETING_QUEUE_MAX);
        }
    }

    /** 全部已结识 NPC id（特殊 + 普通）——特殊 NPC（竞争型实例）已在 NPCS 主表，用 Set 去重同一 id */
    private metNpcIds(): string[] {
        const state = GameState.getInstance();
        const ids = new Set(GameData.NPCS.map(n => n.id));
        state.player.normalNpcs.forEach(n => ids.add(n.id));
        return [...ids].filter(id => state.getFavor(id) >= 0);
    }

    /** 好感档基础概率（档位线 20/40/60/80） */
    private tierProb(favor: number): number {
        for (const t of GameConfig.GREETING_TIER_PROB) {
            if (favor >= t.minFavor) return t.prob;
        }
        return 0;
    }

    /** 打招呼文案池：特殊 NPC 优先用专属 greetingTexts，缺省才走通用 special 池；普通 NPC 按性格模板 */
    private greetingPool(npcId: string): string[] {
        const special = GameData.NPC_MAP[npcId];
        if (special?.greetingTexts?.length) return special.greetingTexts;
        const normal = GameState.getInstance().player.normalNpcs.find(n => n.id === npcId);
        const key: PersonalityKey | 'special' = normal && (PERSONALITY_KEYS as string[]).includes(normal.personality)
            ? (normal.personality as PersonalityKey)
            : 'special';
        return GREETING_LINES[key];
    }

    /** 该 NPC 未读招呼数（人脉页行红点） */
    unreadFor(npcId: string): number {
        return GameState.getInstance().player.greetings.filter(g => g.npcId === npcId && !g.read).length;
    }

    /** 全队列未读招呼数（底部导航「人脉」徽章） */
    totalUnread(): number {
        return GameState.getInstance().player.greetings.filter(g => !g.read).length;
    }

    /** 进入聊天页：该 NPC 招呼全部标已读（红点清除），返回本次清掉的条数（>0 时宿主发 STATE_CHANGED） */
    markRead(npcId: string): number {
        let n = 0;
        for (const g of GameState.getInstance().player.greetings) {
            if (g.npcId === npcId && !g.read) { g.read = true; n++; }
        }
        return n;
    }

    /** 回应最早一条未回应招呼：+1 好感（不占聊天额度），返回两口气泡文案。
     *  ⛔ 客串邀请不走此通道——它有专属抉择口（respondInvite），⛔ 被「回应招呼」钮误消费 */
    respond(npcId: string): GreetingReplyResult {
        const state = GameState.getInstance();
        const g = state.player.greetings.find(x => x.npcId === npcId && !x.replied && x.kind !== 'guestInvite');
        if (!g) return { ok: false, meText: '', npcText: '', gain: 0 };
        g.replied = true;
        g.read = true;
        const gain = GameConfig.GREETING_REPLY_GAIN;
        state.addFavor(npcId, gain);
        const name = socialName(npcId);
        // 用招呼自带的快捷回复语（旧档缺 replyText 兜底现抽一条），保证按钮显示=实际发出；
        // 特殊 NPC 手写招呼的 NPC 再回复优先用 replyNpcText，缺省走通用池
        return { ok: true, meText: g.replyText ?? pick(GREETING_REPLY_ME), npcText: g.replyNpcText ?? pick(GREETING_REPLY_NPC), gain };
    }

    /**
     * 客串邀请抉择（好友客串批①＝机制/交互层，批③＝结算）：
     * 就地写 picked + 定格两口气泡（玩家回话 / NPC 反应），幂等（已抉择返回 null）。
     * 约定 inviteOptions 下标 0=接受、1=婉拒；接受时结算蹭名气+好感（委托 GuestCameoSystem.acceptEffects，
     * 只在 picked 首次落定时跑一次，重开聊天页不重复到账）。
     */
    respondInvite(g: NpcGreeting, idx: number): { meText: string; npcText: string; fameGain: number; favorGain: number } | null {
        if (g.kind !== 'guestInvite' || g.picked !== undefined) return null;
        const accept = idx === 0;
        g.picked = idx;
        g.read = true;
        const meText = pick(accept ? GUEST_INVITE_REPLY_ME.accept : GUEST_INVITE_REPLY_ME.decline);
        const npcText = pick(GUEST_INVITE_REPLY_NPC[this.personalityKey(g.npcId)][accept ? 'accept' : 'decline']);
        g.replyTexts = [meText, npcText];
        const eff = accept ? guestCameoSystem.acceptEffects(g) : { fameGain: 0, favorGain: 0 };
        return { meText, npcText, ...eff };
    }

    /** NPC 性格键（普通 NPC 命中四模板之一则用之，否则 special 通用池） */
    private personalityKey(npcId: string): PersonalityKey | 'special' {
        const normal = GameState.getInstance().player.normalNpcs.find(n => n.id === npcId);
        return normal && (PERSONALITY_KEYS as string[]).includes(normal.personality)
            ? (normal.personality as PersonalityKey) : 'special';
    }
}

function socialName(npcId: string): string {
    const special = GameData.NPC_MAP[npcId];
    if (special) return special.name;
    return GameState.getInstance().player.normalNpcs.find(n => n.id === npcId)?.name ?? npcId;
}

/** 导出单例 */
export const greetingSystem = GreetingSystem.getInstance();
